import { HttpClient, provideHttpClient } from '@angular/common/http';
import { EnvironmentProviders, Injectable, Provider, inject, isDevMode } from '@angular/core';
import { Translation, TranslocoLoader, provideTransloco } from '@jsverse/transloco';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { Observable, catchError, from } from 'rxjs';
import { takePrefetchedTranslation } from './i18n-prefetch';

/**
 * HTTP loader for Transloco — fetches `public/i18n/<lang>/<scope>.json`.
 *
 * Scopes are loaded lazily as feature routes activate (via
 * `TRANSLOCO_SCOPE` providers in each feature's routes file). The root
 * `shared` scope is eager — declared in `availableLangs` defaults and
 * fetched at boot by AppInitializer via TranslocoService.load().
 */
@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  private readonly http = inject(HttpClient);

  getTranslation(langPath: string): Observable<Translation> {
    // langPath is either "en" (root) or "feature/en" (scoped).
    const url = `/i18n/${langPath}.json`;
    // The root dictionary may already be on its way: index.html starts the fetch at
    // first byte (see i18n-prefetch.ts). A failed prefetch falls back to a normal read.
    const early = takePrefetchedTranslation(langPath);
    if (early) {
      return from(early).pipe(catchError(() => this.http.get<Translation>(url)));
    }
    return this.http.get<Translation>(url);
  }
}

/**
 * AVAILABLE_LANGS / DEFAULT_LANG mirror SUPPORTED_LOCALES in LocaleService.
 * Kept here separately so Transloco initialises before LocaleService
 * resolves the user's actual locale during the AppInitializer phase.
 *
 * ADDING A LOCALE takes four edits, and three of them are silent if missed:
 *   1. SUPPORTED_LOCALES in `core/services/locale.service.ts` (the picker).
 *   2. AVAILABLE_LANGS below — an id absent here is never fetched at all.
 *   3. The `locales` array passed to provideTranslocoMessageformat below.
 *   4. `public/i18n/<id>.json`, plus the LOCALES list in
 *      `scripts/check-i18n-parity.mjs` so the gate actually covers it.
 * Only (4) fails loudly; the rest degrade to English, which reads as
 * "translation missing" rather than "locale not wired".
 */
const AVAILABLE_LANGS = [
  { id: 'en', label: 'English' },
  { id: 'fr', label: 'Français' },
  { id: 'de', label: 'Deutsch' },
  { id: 'es', label: 'Español' },
  { id: 'it', label: 'Italiano' },
  { id: 'nl', label: 'Nederlands' },
  { id: 'ja', label: '日本語' },
];
const DEFAULT_LANG = 'en';
const FALLBACK_LANG = 'en';

/**
 * App-level Transloco providers. Returns an array suitable for spreading
 * into ApplicationConfig.providers.
 *
 * Includes provideHttpClient() — Apollo also uses HttpClient elsewhere
 * but the Angular HttpClient provider is idempotent so duplicate
 * provisioning is safe.
 */
export function provideAppTransloco(): (Provider | EnvironmentProviders)[] {
  return [
    provideHttpClient(),
    provideTransloco({
      config: {
        availableLangs: AVAILABLE_LANGS,
        defaultLang: DEFAULT_LANG,
        fallbackLang: FALLBACK_LANG,
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          logMissingKey: isDevMode(),
          useFallbackTranslation: true,
          allowEmpty: false,
        },
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoMessageformat({
      // Plural/select rules are per-LOCALE, not per-language: MessageFormat
      // needs every BCP-47 tag we can switch to or `{count, plural, ...}`
      // silently falls back to English cardinal rules. FR/ES/IT/PT-style
      // "one vs other" and the Slavic-style few/many categories differ, so a
      // missing tag here is a wrong-plural bug, not a missing-string bug.
      locales: ['en-GB', 'fr-FR', 'de-DE', 'es-ES', 'it-IT', 'nl-NL', 'ja-JP'],
    }),
  ];
}
